/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { StructuredText } from 'react-datocms/structured-text'
import {
	Text,
	BreadcrumbItemProps,
	Breadcrumbs,
	Button,
	Badge,
} from '@hashicorp/mds-react/components'
import { vercelSplit } from '@web/cms'
import FullWidthVideo from 'components/materia/full-width-video'
import {
	type IBadge,
	getConferenceBadgeProps,
} from 'app/[locale]/conferences/lib/utils'
import Headshot, { type HeadshotProps } from 'components/materia/headshot'
import { ConferenceSessionQuery, type LayoutFieldsFragment } from 'types/dato'
import { Layout } from 'components/materia/layout'
import SocialShare from 'components/social-share'
import s from './style.module.css'

export interface ConferenceSessionDetailsProps {
	layout: LayoutFieldsFragment
	breadcrumbs?: BreadcrumbItemProps[]
	eyebrow: string
	socialMediaSummary: string
	title: string
	date: string
	subheadline: string
	description: ConferenceSessionQuery['conferenceSession']['description']
	badges: IBadge[]
	headshots: HeadshotProps[]
	videoUrl?: string
	cta?: {
		href: string
		text: string
	}
}

const ConferenceSessionDetails = ({
	layout,
	breadcrumbs,
	eyebrow,
	socialMediaSummary,
	title,
	date,
	subheadline,
	description,
	badges,
	headshots,
	videoUrl,
	cta,
}: ConferenceSessionDetailsProps) => {
	return (
		<>
			<Layout layout={layout}>
				<div className={s.root}>
					{breadcrumbs?.length ? (
						<Breadcrumbs.Root className={s.breadcrumbs}>
							{breadcrumbs.map((item, idx) => (
								<Breadcrumbs.Item
									{...item}
									current={idx === breadcrumbs.length - 1}
									key={item.text}
								/>
							))}
						</Breadcrumbs.Root>
					) : null}
					<div className={s.text}>
						<div className={s.header}>
							<div className={s.headerInner}>
								<Text.Label tag="p" color="faint" className={s.eyebrow}>
									{eyebrow}
								</Text.Label>
								<SocialShare summary={socialMediaSummary} />
							</div>
							<Text.DisplayExpressive
								color="strong"
								className={s.headline}
								tag="h1"
							>
								{title}
							</Text.DisplayExpressive>
							<Text.Body tag="p" color="faint" className={s.date}>
								{date}
							</Text.Body>
						</div>
						<div className={s.details}>
							<Text.DisplayExpressive
								tag="p"
								color="strong"
								size="200"
								className={s.subheadline}
							>
								{subheadline}
							</Text.DisplayExpressive>
							<Text.Body color="primary" size="200" className={s.description}>
								<StructuredText data={description} />
							</Text.Body>
							{badges && badges.length > 0 ? (
								<ul className={s.badges} aria-label="Badges">
									{badges.map((badge) => {
										const badgeProps = getConferenceBadgeProps(badge)
										return (
											<li key={badgeProps.icon}>
												<Badge
													icon={badgeProps.icon}
													iconColor={badgeProps.color}
													text={badgeProps.text}
												/>
											</li>
										)
									})}
								</ul>
							) : null}
							{cta && (
								<div className={s.cta}>
									<Button
										className={s.button}
										{...cta}
										color={
											layout?.appearance &&
											vercelSplit(layout?.appearance) === 'dark'
												? 'secondary-black'
												: 'secondary'
										}
										size="large"
									/>
								</div>
							)}
						</div>
					</div>
					{headshots ? (
						<div className={s.headshotsWrapper}>
							<Headshots headshots={headshots} />
						</div>
					) : null}
				</div>
			</Layout>
			{videoUrl ? (
				<FullWidthVideo
					__typename={'FullWidthVideoRecord'}
					layout={{
						appearance: layout?.appearance,
						paddingTop: 'small',
						paddingBottom: 'large',
					}}
					videoUrl={videoUrl}
				/>
			) : null}
		</>
	)
}

const Headshots = ({ headshots }) => {
	return (
		<ul className={s.headshots}>
			{headshots.map((headshot, index) => {
				return (
					<li key={index} className={s.headshot}>
						<Headshot {...headshot} />
					</li>
				)
			})}
		</ul>
	)
}

export default ConferenceSessionDetails
